import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';

const ViewUserRole = () => {
  const { id } = useParams();
  const [userRoleData, setUserRoleData] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [buData, setBuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch Business Units
      const buResponse = await fetch('https://apidashboard.eurekagroup.id/bu', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (buResponse.ok) {
        const buResult = await buResponse.json();
        setBuData(buResult);
      }

      // Fetch Users
      const usersResponse = await fetch('https://api-sso-v2.eurekagroup.id/api/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (usersResponse.ok) {
        const usersResult = await usersResponse.json();
        setUsers(usersResult.data || usersResult);
      }

      // Fetch Clients
      const clientsResponse = await fetch('https://api-sso-v2.eurekagroup.id/api/clients', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (clientsResponse.ok) {
        const clientsResult = await clientsResponse.json();
        setClients(clientsResult.data || clientsResult);
      }

      // Fetch User Role Data
      const userRoleResponse = await fetch(`https://api-sso-v2.eurekagroup.id/api/user-roles/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (userRoleResponse.ok) {
        const userRoleResult = await userRoleResponse.json();
        const data = userRoleResult.data || userRoleResult;
        setUserRoleData(data);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Gagal memuat data'
      });
      navigate('/sso/user-role');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'superadmin':
        return 'error';
      case 'user':
        return 'success';
      default:
        return 'default';
    }
  };

  const getBuName = (idBu) => {
    const bu = buData.find(item => item.bu_code === idBu.toString());
    return bu ? bu.bu_name : `BU ${idBu}`;
  };

  const getUserName = (userData) => {
    // Handle both object and string cases
    if (typeof userData === 'object' && userData !== null) {
      return userData.nama_lengkap || 'Unknown User';
    }
    const user = users.find(item => item._id === userData);
    return user ? user.nama_lengkap : 'Unknown User';
  };

  const getUserEmail = (userData) => {
    // Handle both object and string cases
    if (typeof userData === 'object' && userData !== null) {
      return userData.email || 'Unknown Email';
    }
    const user = users.find(item => item._id === userData);
    return user ? user.email : 'Unknown Email';
  };

  const getClientName = (clientData) => {
    // Handle both object and string cases
    if (typeof clientData === 'object' && clientData !== null) {
      return clientData.client || 'Unknown Client';
    }
    const client = clients.find(item => item._id === clientData);
    return client ? client.client : 'Unknown Client';
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data user role...
        </Typography>
      </Box>
    );
  }

  if (!userRoleData) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Data user role tidak ditemukan
        </Typography>
      </Box>
    );
  }

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <VpnKeyIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Detail User Role
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Informasi lengkap user role
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/sso/user-role')}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Kembali
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sso/user-role/edit/${id}`)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Edit User Role
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Informasi User
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Nama User
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {getUserName(userRoleData.id_user)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getUserEmail(userRoleData.id_user)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      User ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
                      {typeof userRoleData.id_user === 'object' ? userRoleData.id_user._id : userRoleData.id_user}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Informasi Client & Role
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Client
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {getClientName(userRoleData.id_client)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Role
                    </Typography>
                    <Chip 
                      label={userRoleData.role}
                      color={getRoleColor(userRoleData.role)}
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Client ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
                      {typeof userRoleData.id_client === 'object' ? userRoleData.id_client._id : userRoleData.id_client}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Informasi Business Unit
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Business Unit
                    </Typography>
                    <Chip 
                      label={getBuName(userRoleData.id_bu)}
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      BU Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {userRoleData.id_bu}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewUserRole;